import { useState, useEffect } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Calculator, DollarSign, PieChart } from 'lucide-react';

const emptyCalc = { client_id: '', period_start: '', period_end: '' };

export default function PerformancePage() {
  const [fees, setFees] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyCalc);
  const [result, setResult] = useState(null);

  const fetchData = async () => {
    try {
      const [fRes, cRes] = await Promise.all([api.get('/performance'), api.get('/clients')]);
      setFees(fRes.data);
      setClients(cRes.data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  const handleCalculate = async () => {
    try {
      const { data } = await api.post('/performance/calculate', form);
      setResult(data);
      toast.success('Performance fee calculated');
      fetchData();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const handleSettle = async (id) => {
    try { await api.put(`/performance/${id}/settle`); toast.success('Fee settled'); fetchData(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '-';
  const totalCompanyShare = fees.reduce((s, f) => s + (f.company_share || 0), 0);
  const totalInvestorProfit = fees.reduce((s, f) => s + (f.investor_profit || 0), 0);

  return (
    <div className="space-y-6" data-testid="performance-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-brand-text-primary tracking-tight">Performance Fees</h1>
          <p className="text-sm text-brand-text-secondary mt-1">Profit sharing calculations and settlements</p>
        </div>
        <Button onClick={() => { setForm(emptyCalc); setResult(null); setDialogOpen(true); }} className="bg-brand-secondary hover:bg-brand-secondary-hover text-white rounded-sm" data-testid="calculate-fee-btn">
          <Calculator size={16} className="mr-2" /> Calculate Fee
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-brand-primary/10 flex items-center justify-center"><DollarSign size={20} className="text-brand-primary" /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary">Company Share</p>
              <p className="font-heading text-2xl text-brand-text-primary">${totalCompanyShare.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-brand-border bg-white">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-10 h-10 rounded-sm bg-brand-accent/15 flex items-center justify-center"><PieChart size={20} className="text-brand-accent" /></div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-text-secondary">Investor Profit</p>
              <p className="font-heading text-2xl text-brand-text-primary">${totalInvestorProfit.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-brand-border bg-white">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="performance-table">
              <thead>
                <tr className="border-b border-brand-border bg-brand-bg/50">
                  {['Client', 'Period', 'Net Profit', 'Fee %', 'Company Share', 'Investor Profit', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left py-3 px-4 text-xs uppercase tracking-[0.15em] font-semibold text-brand-text-secondary">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">Loading...</td></tr> :
                 fees.length === 0 ? <tr><td colSpan={8} className="py-12 text-center text-brand-text-secondary">No performance fees calculated</td></tr> :
                 fees.map(f => (
                  <tr key={f.id} className="border-b border-brand-border/50 hover:bg-brand-surface-hover transition-colors">
                    <td className="py-3 px-4 text-brand-text-primary">{getClientName(f.client_id)}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{f.period_start} to {f.period_end}</td>
                    <td className={`py-3 px-4 font-medium ${f.net_profit >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${Number(f.net_profit).toLocaleString()}</td>
                    <td className="py-3 px-4 text-brand-text-secondary">{f.fee_percentage}%</td>
                    <td className="py-3 px-4 font-medium text-brand-text-primary">${Number(f.company_share).toLocaleString()}</td>
                    <td className="py-3 px-4 font-medium text-brand-text-primary">${Number(f.investor_profit).toLocaleString()}</td>
                    <td className="py-3 px-4">
                      <Badge className={`text-xs border-0 ${f.status === 'settled' ? 'bg-brand-success/15 text-brand-success' : 'bg-amber-100 text-amber-700'}`}>{f.status}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      {f.status === 'calculated' && (
                        <Button variant="outline" size="sm" onClick={() => handleSettle(f.id)} className="text-xs border-brand-border rounded-sm" data-testid={`settle-${f.id}`}>Settle</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="performance-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Calculate Performance Fee</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={v => setForm({...form, client_id: v})}>
                <SelectTrigger className="bg-white border-brand-border rounded-sm" data-testid="perf-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Period Start *</Label>
              <Input type="date" value={form.period_start} onChange={e => setForm({...form, period_start: e.target.value})} data-testid="perf-start-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Period End *</Label>
              <Input type="date" value={form.period_end} onChange={e => setForm({...form, period_end: e.target.value})} data-testid="perf-end-input" className="bg-white border-brand-border rounded-sm" />
            </div>
            {result && (
              <Card className="border border-brand-border bg-brand-bg">
                <CardContent className="p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-brand-text-secondary">Net Profit:</span><span className={`font-medium ${result.net_profit >= 0 ? 'text-brand-success' : 'text-brand-error'}`}>${Number(result.net_profit).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-brand-text-secondary">Fee ({result.fee_percentage}%):</span><span className="font-medium text-brand-text-primary">${Number(result.company_share).toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-brand-text-secondary">Investor Profit:</span><span className="font-medium text-brand-text-primary">${Number(result.investor_profit).toLocaleString()}</span></div>
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-brand-border rounded-sm">Close</Button>
            <Button onClick={handleCalculate} className="bg-brand-secondary hover:bg-brand-secondary-hover text-white rounded-sm" data-testid="perf-calculate-btn">Calculate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
